import { useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { useHasPermission } from '@/components/PermissionGate';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { CommandResultSnackbar, type CommandResult } from '@/components/CommandResultSnackbar';
import { ValidationError, toFormErrors } from '@/lib/validationErrors';
import type { ListViewExtraProps } from '@/config/listViewExtras';
import type { MenuActionExtraDialogProps } from '@/config/menuActionExtras';
import type { PlanLineReevaluationRun } from '@/types/finance/PlanLineReevaluationRun';

/** Controller capability behind the action — global, not org-bound (spec §4). */
export const REEVALUATE_LINES_PERMISSION = 'Finance.PlanHeaderActions.ReevaluateLines';

/** What the run's detail view reads with — starting a run does not imply being allowed to open it. */
const RUN_READ_PERMISSION = 'Finance.PlanLineReevaluationRun.Read';

/** Header statuses the client treats as eligible; the server's guard set (4027–4030) is authoritative. */
const ELIGIBLE_STATUSES: ReadonlySet<unknown> = new Set(['DRAFT', 'RETURNED']);

interface StartRequest {
  planHeaderIds: string[];
  idempotencyKey: string;
}

interface Receipt {
  runId: string;
  result: CommandResult;
}

function isEligible(row: Record<string, unknown>): boolean {
  return ELIGIBLE_STATUSES.has(row.status);
}

/**
 * Queued/skipped for the receipt, from the server's verdict per item (the client's preview only
 * knows the row status; the server also sees frozen versions and runs already in flight). Falls
 * back to the run counters when the response carries no items.
 */
function countRun(run: PlanLineReevaluationRun): { queued: number; skipped: number } {
  const items = run.items ?? [];
  if (items.length === 0) {
    return { queued: run.headerCount - run.skippedCount, skipped: run.skippedCount };
  }
  const skipped = items.filter((item) => item.status === 'SKIPPED').length;
  return { queued: items.length - skipped, skipped };
}

async function startReevaluation(request: StartRequest): Promise<PlanLineReevaluationRun> {
  const response = await apiClient.post<PlanLineReevaluationRun>(
    '/api/finance/planheader/reevaluate-lines',
    request,
  );
  return response.data;
}

/** One header, answered when done (the finished run, not a receipt of a queued one). */
async function reevaluateNow(planHeaderId: string, idempotencyKey: string): Promise<PlanLineReevaluationRun> {
  const response = await apiClient.post<PlanLineReevaluationRun>(
    `/api/finance/planheader/${planHeaderId}/reevaluate-lines-now`,
    { idempotencyKey },
  );
  return response.data;
}

interface ReevaluateLinesFlowProps {
  /** Posts the request; the idempotency key is fresh per confirm. */
  start: (idempotencyKey: string) => Promise<PlanLineReevaluationRun>;
  confirmOpen: boolean;
  confirmMessage: string;
  /** What the receipt says about the returned run. */
  receiptFor: (run: PlanLineReevaluationRun) => CommandResult;
  onCancel: () => void;
  /** The server answered with the run — the caller closes its confirm. */
  onAccepted?: () => void;
  /** The receipt was dismissed or followed to the run. */
  onDone?: () => void;
}

/**
 * Starting a PlanLineReevaluationRun, shared by both entry points — the Plan Headers grid toolbar
 * (a selection) and the Plan Header Actions menu (the header in hand): the confirm, the POST, the
 * server's refusal inside the dialog and the receipt in the command-result snackbar.
 */
function ReevaluateLinesFlow({
  start,
  confirmOpen,
  confirmMessage,
  receiptFor,
  onCancel,
  onAccepted,
  onDone,
}: ReevaluateLinesFlowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canOpenRun = useHasPermission(RUN_READ_PERMISSION);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const [receipt, setReceipt] = useState<Receipt | null>(null);

  const mutation = useMutation({
    mutationFn: start,
    onSuccess: (run) => {
      setReceipt({ runId: run.id, result: receiptFor(run) });
      onAccepted?.();
    },
    // The 409 guard outcomes and 500s carry the server's own message; generic text is the last resort.
    onError: (error: unknown) => setApiValidationErrors(
      toFormErrors(error, t('common.genericError', { defaultValue: 'An error occurred' })),
    ),
  });

  const label = t('Finance.MenuAction.ReevaluatePlanHeaderLines', { defaultValue: 'Re-evaluate lines' });

  const handleConfirm = () => {
    setApiValidationErrors([]);
    // A fresh key per confirm: one attempt replays on a network retry, a deliberate second
    // confirm starts a second run.
    mutation.mutate(crypto.randomUUID());
  };

  const handleCancel = () => {
    setApiValidationErrors([]);
    onCancel();
  };

  const closeReceipt = () => {
    setReceipt(null);
    onDone?.();
  };

  const openRun = () => {
    if (receipt) navigate(`/finance/planlinereevaluationruns/${receipt.runId}`);
    closeReceipt();
  };

  return (
    <>
      <ConfirmDialog
        open={confirmOpen}
        title={label}
        message={confirmMessage}
        confirmLabel={mutation.isPending
          ? t('Finance.ReevaluatePlanHeaderLines.Running', { defaultValue: 'Re-evaluating…' })
          : label}
        cancelLabel={t('common.cancel', { defaultValue: 'Cancel' })}
        confirmDisabled={mutation.isPending}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      >
        <DialogValidationErrors errors={apiValidationErrors} onClear={() => setApiValidationErrors([])} />
      </ConfirmDialog>

      {/* The outcome goes through the app's one command-result snackbar; the run's progress
          and completion reach the user through the notification bell. */}
      <CommandResultSnackbar
        result={receipt?.result ?? null}
        onClose={closeReceipt}
        action={canOpenRun ? (
          <Button color="inherit" size="small" onClick={openRun}>
            {t('Finance.ReevaluatePlanHeaderLines.OpenRun', { defaultValue: 'Open run' })}
          </Button>
        ) : undefined}
      />
    </>
  );
}

/**
 * Toolbar action on the Plan Headers grid: "Re-evaluate lines" — replay the selected headers'
 * lines against the current account rules, dimension rules and internal price lists as one
 * PlanLineReevaluationRun. Spec: PlanBudgetDatamodell → Re-evaluation run → UI surface.
 *
 * Every selected id is POSTed; the server decides what to skip. The client's eligibility check
 * (status DRAFT/RETURNED) only drives the disabled state and the confirm preview, so the receipt —
 * the server's verdict — can differ from the preview when a version is frozen or a run is already
 * in flight.
 */
export function ReevaluatePlanHeaderLinesButton({
  selectedIds,
  selectedRows,
  onActionCompleted,
}: ListViewExtraProps) {
  const { t } = useTranslation();
  const allowed = useHasPermission(REEVALUATE_LINES_PERMISSION);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!allowed) return null;

  const eligibleCount = selectedRows.filter(isEligible).length;
  const ineligibleCount = selectedRows.length - eligibleCount;
  const label = t('Finance.MenuAction.ReevaluatePlanHeaderLines', { defaultValue: 'Re-evaluate lines' });
  const hint = eligibleCount > 0
    ? label
    : t('Finance.ReevaluatePlanHeaderLines.PickEligible', {
        defaultValue: 'Select at least one plan header in Draft or Returned',
      });

  const confirmMessage = [
    t('Finance.ReevaluatePlanHeaderLines.Confirm', {
      defaultValue:
        'Re-evaluate the lines of {{count}} plan header(s) against the current account rules, dimension rules '
        + 'and internal price lists? Entered figures are kept; lines that can no longer be derived become Pending.',
      count: eligibleCount,
    }),
    ineligibleCount > 0
      ? t('Finance.ReevaluatePlanHeaderLines.WillSkip', {
          defaultValue: '{{count}} selected header(s) are not in Draft or Returned and will be skipped.',
          count: ineligibleCount,
        })
      : null,
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* A disabled MUI Button swallows pointer events, so the tooltip needs a wrapper to hear them. */}
      <Tooltip title={hint}>
        <span>
          <Button
            startIcon={<ReplayIcon />}
            disabled={eligibleCount === 0}
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {label}
          </Button>
        </span>
      </Tooltip>

      <ReevaluateLinesFlow
        start={(idempotencyKey) => startReevaluation({ planHeaderIds: selectedIds, idempotencyKey })}
        confirmOpen={confirmOpen}
        confirmMessage={confirmMessage}
        receiptFor={(run) => ({
          severity: 'success',
          message: t('Finance.ReevaluatePlanHeaderLines.Started', {
            defaultValue: 'Re-evaluation started: {{queued}} header(s) queued, {{skipped}} skipped.',
            ...countRun(run),
          }),
        })}
        onCancel={() => setConfirmOpen(false)}
        onAccepted={() => {
          setConfirmOpen(false);
          onActionCompleted?.();
        }}
      />
    </>
  );
}

/**
 * The finished single-header run as the user reads it: the run's own failure, the header's, lines
 * left pending (worth a look), or done. A header that may not be re-evaluated never gets here — it
 * starts no run and the refusal shows in the confirm.
 */
function singleHeaderReceipt(
  run: PlanLineReevaluationRun,
  t: (key: string, options: Record<string, unknown>) => string,
): CommandResult {
  const item = run.items?.[0];
  if (run.status === 'FAILED' || item?.status === 'FAILED') {
    return {
      severity: 'error',
      message: t('Finance.ReevaluatePlanHeaderLines.Failed', {
        defaultValue: 'The re-evaluation failed: {{reason}}',
        reason: item?.reason ?? run.errorSummary ?? '',
      }),
    };
  }
  const pending = run.linesPendingAfter ?? 0;
  if (pending > 0) {
    return {
      severity: 'warning',
      message: t('Finance.ReevaluatePlanHeaderLines.DonePending', {
        defaultValue: 'The plan lines are re-evaluated; {{count}} line(s) are pending.',
        count: pending,
      }),
    };
  }
  return {
    severity: 'success',
    message: t('Finance.ReevaluatePlanHeaderLines.Done', { defaultValue: 'The plan lines are re-evaluated.' }),
  };
}

/**
 * The Plan Header Actions menu entry (detail view and entry grid): re-evaluate the lines of the
 * header in hand and wait for it — one header replays in about a second, so the run executes in the
 * request and the open grid refreshes on the answer instead of on a notification. Mounted by the
 * menu when chosen; closes when the confirm is cancelled or the receipt is dismissed.
 */
export function ReevaluatePlanHeaderLinesDialog({ entityId, open, onClose }: MenuActionExtraDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(open);

  return (
    <ReevaluateLinesFlow
      start={(idempotencyKey) => reevaluateNow(entityId, idempotencyKey)}
      confirmOpen={confirmOpen}
      confirmMessage={t('Finance.ReevaluatePlanHeaderLines.ConfirmSingle', {
        defaultValue:
          'Re-evaluate this plan header\'s lines against the current account rules, dimension rules and internal '
          + 'price lists? Entered figures are kept; lines that can no longer be derived become Pending.',
      })}
      receiptFor={(run) => singleHeaderReceipt(run, t)}
      onCancel={onClose}
      onAccepted={() => {
        setConfirmOpen(false);
        // The lines were rewritten: every plan-line query refetches, the entry grid's cells included.
        void queryClient.invalidateQueries({ queryKey: ['planlines'] });
      }}
      onDone={onClose}
    />
  );
}
